import { Badge } from "@package/subframe/components/Badge";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherChevronRight } from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { useNursery } from "@/contexts/NurseryContext";
import { useListTodaySessionsQueryControllerList } from "@/generated/log-types/log-types";
import { isLogSessionSlug } from "@/lib/log-session-slugs";

export default function LogsPage() {
  const { selectedNurseryId } = useNursery();
  const { data: sessionsResponse, isLoading } =
    useListTodaySessionsQueryControllerList(selectedNurseryId ?? "");
  // Hot holding is excluded here on purpose — it has its own board and never
  // appeared on this page.
  const sessions = sessionsResponse?.data?.filter(
    (s) =>
      isLogSessionSlug(s.logTypeSlug) &&
      s.logTypeSlug !== "hot-holding-temperature",
  );

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 overflow-y-auto bg-white">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-metrics font-metrics text-primary-navy-800">
          Monitoring
        </span>
        <span className="text-body font-body text-subtext-color">
          Record daily observations and readings
        </span>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : !sessions || sessions.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
          <span className="text-body font-body text-subtext-color">
            No log types configured for this nursery
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-2">
          {sessions.map((session) => (
            <Link
              key={session.logTypeId}
              to="/logs/$logTypeId"
              params={{ logTypeId: session.logTypeId }}
              className="w-full"
            >
              <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="flex grow flex-col items-start gap-0.5">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {session.logTypeName}
                  </span>
                  {session.logTypeDescription && (
                    <span className="text-caption font-caption text-subtext-color">
                      {session.logTypeDescription}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {session.entryCount > 0 && (
                    <Badge variant="neutral">
                      {session.entryCount}{" "}
                      {session.entryCount === 1 ? "entry" : "entries"}
                    </Badge>
                  )}
                  <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
